import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/core/ui/components/Button';
import { Input } from '@/core/ui/components/Input';
import type { HouseholdMember } from '@/shared/types';

type HouseholdRelation = HouseholdMember['relation'];

interface FamilyMemberModalProps {
  onClose: () => void;
  onSave: (member: Omit<HouseholdMember, 'id' | 'accountStatus'>) => Promise<void>;
}

export const FamilyMemberModal: React.FC<FamilyMemberModalProps> = ({ onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<Partial<HouseholdMember>>({ relation: 'OTHER' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setLoading(true);
    try {
      await onSave({
        name: form.name,
        relation: form.relation as HouseholdRelation || 'OTHER',
        age: form.age,
        email: form.email,
        phone: scrubToDigits(form.phone || '')
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const scrubToDigits = (val) => val.replace(/\D/g, '').slice(0, 10);

  const formatForDisplay = (digits) => {
    if (!digits) return '';
    const d = digits;
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)} - ${d.slice(6, 10)}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex justify-between items-center p-5 border-b border-slate-100">
          <h3 className="font-bold text-slate-800 text-lg">Registrar Familiar</h3>
          <button onClick={onClose}><X size={20} className="text-slate-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input label="Nombre Completo" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Relación / Parentesco</label>
            <select className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm" value={form.relation} onChange={e => setForm({ ...form, relation: e.target.value as HouseholdRelation })}>
              <option value="SPOUSE">Cónyuge</option>
              <option value="CHILD">Hijo/a</option>
              <option value="PARENT">Padre/Madre</option>
              <option value="OTHER">Otro</option>
            </select>
          </div>
          <Input label="Email" type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} required />
          <p className="text-xs text-slate-500 -mt-2">Se enviará un enlace de acceso a este correo.</p>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Edad" type="number" value={form.age || ''} onChange={e => setForm({ ...form, age: parseInt(e.target.value) })} />
            <Input
              label="Teléfono"
              type="tel"
              // DISPLAY the formatted version
              value={formatForDisplay(form.phone)}
              // SAVE only the raw digits
              onChange={e => setForm({
                ...form,
                phone: scrubToDigits(e.target.value)
              })}
            />

          </div>
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="primary" className="flex-1" isLoading={loading}>Guardar</Button>
          </div>
        </form>
      </div>
    </div>
  );
};
